import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/common/button/Button";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import Filters from "../filters/Filters";
import styles from "./TaxReport.module.scss";
import AuditService, {
  AuditFilterParams,
} from "../../../api/services/audit.service";
import { useCurrencies } from "../../../context/CurrenciesContext";

const today = new Date();
const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1)
  .toISOString()
  .slice(0, 10);
const defaultEnd = today.toISOString().slice(0, 10);

interface TaxRow {
  store_id: number;
  store_name: string;
  platform_profit: number;
  tax_due: number;
  tax_paid: number;
  tax_unpaid: number;
  records: number;
}

const TaxReport = () => {
  const { t } = useTranslation("translation");
  const [storeId, setStoreId] = useState("");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [rows, setRows] = useState<TaxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const { currencyId, currency } = useCurrencies();

  const formatMoney = (val?: number) =>
    `${(val ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency || ""}`;

  const columns: Column[] = useMemo(
    () => [
      { key: "store_name", header: t("tax_report.table.store") as string },
      {
        key: "platform_profit",
        header: t("tax_report.table.platform_profit") as string,
        render: formatMoney,
      },
      {
        key: "tax_due",
        header: t("tax_report.table.tax_total") as string,
        render: formatMoney,
      },
      {
        key: "tax_paid",
        header: t("tax_report.table.tax_paid") as string,
        render: formatMoney,
      },
      {
        key: "tax_unpaid",
        header: t("tax_report.table.tax_unpaid") as string,
        render: formatMoney,
      },
      { key: "records", header: t("tax_report.table.records") as string },
    ],
    [currency, t],
  );

  const fetchData = async (params: AuditFilterParams) => {
    setLoading(true);
    try {
      const res = await AuditService.getTaxReport(params);
      setRows(res.data?.data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const params: AuditFilterParams = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (storeId) params.store_id = storeId;
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };

  const handleReset = () => {
    setStoreId("");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };

  useEffect(() => {
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId]);

  return (
    <>
      <Filters
        storeId={storeId}
        startDate={startDate}
        endDate={endDate}
        onChangeStore={setStoreId}
        onChangeStartDate={setStartDate}
        onChangeEndDate={setEndDate}
        onApply={handleApply}
        onReset={handleReset}
      />
      <div className={styles.tax}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{t("tax_report.title") as string}</h2>
            <p className={styles.subtitle}>
              {t("tax_report.subtitle") as string}
            </p>
          </div>
          <Button className={styles.export_btn}>
            {t("tax_report.export_button") as string}
          </Button>
        </div>
        <DataTable
          data={rows}
          columns={columns}
          no_borders
          className={styles.table}
          loading={loading}
        />
      </div>
    </>
  );
};

export default TaxReport;
